/**
 * Command System Initialization
 *
 * Entry point for the command system. Registers all commands with both:
 * 1. The InputSystem (for keyboard shortcuts)
 * 2. The local registry (for UI metadata: labels, icons, dynamic visibility)
 *
 * Usage:
 * ```typescript
 * import { initCommands } from './commands';
 *
 * const cleanup = initCommands(editor);
 * // Call cleanup() on unmount
 * ```
 */

import { get } from 'svelte/store';
import type { CanvasEditor } from '@rool/editor';
import type { InputSystem } from '@rool/input';
import { registerCommand, clearRegistry } from './registry';
import type { UINode } from '@rool/renderer';
import * as viewActions from './actions/view';
import * as nodeActions from './actions/nodes';
import * as layoutActions from './actions/layout';
import * as historyActions from './actions/history';
import { nodes as nodesStore, channel, selectedNodeIds } from '../stores';

/**
 * Initialize the command system.
 * Registers all commands and returns a cleanup function.
 */
export function initCommands(editor: CanvasEditor): () => void {
	const input = editor.getInputSystem();

	// Clear any existing registrations
	clearRegistry();

	// Register view commands
	registerViewCommands(editor, input);

	// Register node commands
	registerNodeCommands(editor, input);

	// Register edit commands (clipboard meta + undo/redo with shortcuts)
	registerEditCommands(input);

	// Register debug commands
	registerDebugCommands(editor);

	return () => {
		clearRegistry();
	};
}

/**
 * Register view commands (zoom, fit, etc.)
 */
function registerViewCommands(editor: CanvasEditor, input: InputSystem): void {
	// Zoom In - already registered in InputSystem by camera behavior
	registerCommand(
		'view.zoom-in',
		{
			label: 'Zoom In',
			category: 'View',
			description: 'Increase zoom level',
		},
		() => viewActions.zoomIn(editor),
	);

	// Zoom Out - already registered in InputSystem by camera behavior
	registerCommand(
		'view.zoom-out',
		{
			label: 'Zoom Out',
			category: 'View',
			description: 'Decrease zoom level',
		},
		() => viewActions.zoomOut(editor),
	);

	// Reset Zoom - already registered in InputSystem by camera behavior
	registerCommand(
		'view.reset-zoom',
		{
			label: 'Reset Zoom',
			category: 'View',
			description: 'Reset zoom to 100%',
		},
		() => viewActions.resetZoom(editor),
	);

	// Fit to Content
	registerCommand(
		'view.fit-to-content',
		{
			label: 'Fit to Content',
			category: 'View',
			description: 'Fit view to all nodes or selection',
		},
		() => viewActions.fitToContent(editor),
	);

	// F key shortcut for fit-to-content
	input.on(
		'keydown',
		'f',
		{
			command: 'view.fit-to-content',
			category: 'view',
			mode: 'default',
			excludeModes: ['editing'],
		},
		() => {
			viewActions.fitToContent(editor);
		},
	);

	// Reset View
	registerCommand(
		'view.reset',
		{
			label: 'Reset View',
			category: 'View',
			description: 'Reset view to origin at 100% zoom',
		},
		() => viewActions.resetView(editor),
	);
}

/**
 * Register node operation commands.
 */
function registerNodeCommands(editor: CanvasEditor, input: InputSystem): void {
	// Select All
	registerCommand(
		'edit.select-all',
		{
			label: 'Select All',
			category: 'Edit',
			description: 'Select all nodes and edges',
		},
		() => nodeActions.selectAll(editor),
	);

	// Note: mod+a shortcut is registered by @rool/editor's selection behavior
	// (selection.ts). Only the command metadata is registered here for palette/menu.

	// Clear Selection (Escape - but Escape is often overloaded)
	registerCommand(
		'edit.clear-selection',
		{
			label: 'Clear Selection',
			category: 'Edit',
			description: 'Deselect all items',
			showInContextMenu: false, // Don't show in context menu
		},
		() => nodeActions.clearSelection(editor),
	);

	// Delete - already registered in InputSystem by @rool/editor
	// Just add registry metadata with dynamic label
	registerCommand(
		'edit.delete',
		{
			label: 'Delete',
			category: 'Canvas (Selection)',
			section: 'Edit',
			getLabel: (selectedNodes: UINode[]) =>
				nodeActions.getDeleteLabel(selectedNodes.length),
		},
		() => nodeActions.deleteSelection(input),
	);

	// Select Descendants
	registerCommand(
		'edit.select-descendants',
		{
			label: 'Select Descendants',
			category: 'Canvas (Selection)',
			section: 'Selection',
			description: 'Select all nodes connected below the selection',
			getLabel: (selectedNodes: UINode[]) =>
				nodeActions.getSelectDescendantsLabel(selectedNodes.length),
		},
		() => nodeActions.selectDescendants(editor),
	);

	// Auto-Fit to Content (keyboard shortcut)
	registerCommand(
		'node.auto-fit',
		{
			label: 'Auto-Fit to Content',
			category: 'Canvas (Selection)',
			section: 'Layout',
			description: 'Resize selected nodes to fit their content',
			showInContextMenu: false, // Hidden from menu, replaced by submenu
			getLabel: (selectedNodes: UINode[]) =>
				nodeActions.getAutoFitLabel(selectedNodes.length),
		},
		() => nodeActions.autoFitSelection(editor),
	);

	// Register shortcut for Auto-Fit
	input.on(
		'keydown',
		'mod+shift+f',
		{
			mode: 'default',
			excludeModes: ['editing'],
			command: { id: 'node.auto-fit', name: 'Auto-Fit to Content' },
			category: 'Layout',
		},
		(ctx) => {
			ctx.stopPropagation();
			nodeActions.autoFitSelection(editor);
		},
	);

	// Auto-Fit submenu with checkbox toggles
	const persistFlags = async (
		nodeId: string,
		data: Record<string, unknown>,
	) => {
		const currentChannel = get(channel);
		if (currentChannel) {
			await currentChannel.updateObject(nodeId, { data });
		}
	};

	registerCommand(
		'node.autofit-width',
		{
			label: 'Auto-Fit Width',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Auto-Fit',
			getLabel: (selectedNodes: UINode[]) =>
				selectedNodes.length > 0 ? 'Auto-Fit Width' : false,
			checked: (selectedNodes: UINode[]) =>
				nodeActions.getAutoFitFlagChecked(selectedNodes, 'autoFitWidth'),
		},
		() => nodeActions.toggleAutoFitFlag(editor, 'autoFitWidth', persistFlags),
	);

	registerCommand(
		'node.autofit-height',
		{
			label: 'Auto-Fit Height',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Auto-Fit',
			getLabel: (selectedNodes: UINode[]) =>
				selectedNodes.length > 0 ? 'Auto-Fit Height' : false,
			checked: (selectedNodes: UINode[]) =>
				nodeActions.getAutoFitFlagChecked(selectedNodes, 'autoFitHeight'),
		},
		() => nodeActions.toggleAutoFitFlag(editor, 'autoFitHeight', persistFlags),
	);

	registerCommand(
		'node.autosize-text',
		{
			label: 'Auto-Size Text',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Auto-Fit',
			getLabel: (selectedNodes: UINode[]) =>
				selectedNodes.length > 0 ? 'Auto-Size Text' : false,
			checked: (selectedNodes: UINode[]) =>
				nodeActions.getAutoFitFlagChecked(selectedNodes, 'autoSizeText'),
		},
		() => nodeActions.toggleAutoFitFlag(editor, 'autoSizeText', persistFlags),
	);

	registerCommand(
		'node.word-wrap',
		{
			label: 'Word Wrap',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Auto-Fit',
			getLabel: (selectedNodes: UINode[]) =>
				selectedNodes.length > 0 ? 'Word Wrap' : false,
			checked: (selectedNodes: UINode[]) =>
				nodeActions.getAutoFitFlagChecked(selectedNodes, 'wordWrap'),
		},
		() => nodeActions.toggleAutoFitFlag(editor, 'wordWrap', persistFlags),
	);

	// Layout submenu: Auto, Organic, Sort
	registerCommand(
		'layout.auto',
		{
			label: 'Auto',
			category: 'Canvas',
			section: 'Layout',
			submenu: 'Layout',
			description: 'Auto-detect best layout algorithm',
		},
		() => layoutActions.layoutSelection(editor),
	);

	registerCommand(
		'layout.organic',
		{
			label: 'Organic',
			category: 'Canvas',
			section: 'Layout',
			submenu: 'Layout',
			description: 'Force-directed organic layout',
		},
		() => layoutActions.layoutForce2(editor),
	);

	registerCommand(
		'layout.sort',
		{
			label: 'Sort',
			category: 'Canvas',
			section: 'Layout',
			submenu: 'Layout',
			description: 'Masonry gallery-wall arrangement',
		},
		() => layoutActions.layoutSort(editor),
	);

	registerCommand(
		'layout.grid',
		{
			label: 'Grid',
			category: 'Canvas',
			section: 'Layout',
			submenu: 'Layout',
			description: 'Uniform grid arrangement',
		},
		() => layoutActions.layoutGrid(editor),
	);

	registerCommand(
		'layout.flow',
		{
			label: 'Flow',
			category: 'Canvas',
			section: 'Layout',
			submenu: 'Layout',
			description: 'DAG rank-based flow layout',
		},
		() => layoutActions.layoutFlow(editor),
	);

	registerCommand(
		'layout.tree',
		{
			label: 'Tree',
			category: 'Canvas',
			section: 'Layout',
			submenu: 'Layout',
			description: 'Hierarchical tree layout',
		},
		() => layoutActions.layoutTree(editor),
	);

	// Align submenu (requires 2+ selected nodes)
	registerCommand(
		'align.left',
		{
			label: 'Align Left',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Align',
			icon: 'AlignLeft',
			getLabel: (sel: UINode[]) =>
				layoutActions.getAlignLabel('Align Left', sel.length),
		},
		() => layoutActions.alignLeft(editor),
	);

	registerCommand(
		'align.center',
		{
			label: 'Align Center',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Align',
			icon: 'AlignCenterHorizontal',
			getLabel: (sel: UINode[]) =>
				layoutActions.getAlignLabel('Align Center', sel.length),
		},
		() => layoutActions.alignCenter(editor),
	);

	registerCommand(
		'align.right',
		{
			label: 'Align Right',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Align',
			icon: 'AlignRight',
			getLabel: (sel: UINode[]) =>
				layoutActions.getAlignLabel('Align Right', sel.length),
		},
		() => layoutActions.alignRight(editor),
	);

	registerCommand(
		'align.top',
		{
			label: 'Align Top',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Align',
			icon: 'AlignTop',
			getLabel: (sel: UINode[]) =>
				layoutActions.getAlignLabel('Align Top', sel.length),
		},
		() => layoutActions.alignTop(editor),
	);

	registerCommand(
		'align.middle',
		{
			label: 'Align Middle',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Align',
			icon: 'AlignCenterVertical',
			getLabel: (sel: UINode[]) =>
				layoutActions.getAlignLabel('Align Middle', sel.length),
		},
		() => layoutActions.alignMiddle(editor),
	);

	registerCommand(
		'align.bottom',
		{
			label: 'Align Bottom',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Align',
			icon: 'AlignBottom',
			getLabel: (sel: UINode[]) =>
				layoutActions.getAlignLabel('Align Bottom', sel.length),
		},
		() => layoutActions.alignBottom(editor),
	);

	registerCommand(
		'distribute.horizontal',
		{
			label: 'Distribute Horizontally',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Align',
			icon: 'ArrowsOutLineHorizontal',
			getLabel: (sel: UINode[]) =>
				layoutActions.getAlignLabel('Distribute Horizontally', sel.length),
		},
		() => layoutActions.distributeHorizontal(editor),
	);

	registerCommand(
		'distribute.vertical',
		{
			label: 'Distribute Vertically',
			category: 'Canvas (Selection)',
			section: 'Layout',
			submenu: 'Align',
			icon: 'ArrowsOutLineVertical',
			getLabel: (sel: UINode[]) =>
				layoutActions.getAlignLabel('Distribute Vertically', sel.length),
		},
		() => layoutActions.distributeVertical(editor),
	);

	// Register shortcut for Auto-Layout (default: auto algorithm)
	input.on(
		'keydown',
		'mod+shift+l',
		{
			mode: 'default',
			excludeModes: ['editing'],
			command: { id: 'layout.auto', name: 'Auto-Layout' },
			category: 'Layout',
		},
		(ctx) => {
			ctx.stopPropagation();
			layoutActions.layoutSelection(editor);
		},
	);
}

/**
 * Register edit commands: clipboard metadata + undo/redo with shortcuts.
 * Clipboard shortcuts are handled by @rool/editor's clipboard behavior;
 * undo/redo shortcuts are registered here.
 */
function registerEditCommands(input: InputSystem): void {
	// Copy
	registerCommand(
		'clipboard.copy',
		{
			label: 'Copy',
			category: 'Edit',
			section: 'Clipboard',
		},
		() => {}, // Execute handled by InputSystem
	);

	// Cut
	registerCommand(
		'clipboard.cut',
		{
			label: 'Cut',
			category: 'Edit',
			section: 'Clipboard',
		},
		() => {}, // Execute handled by InputSystem
	);

	// Paste
	registerCommand(
		'clipboard.paste',
		{
			label: 'Paste',
			category: 'Edit',
			section: 'Clipboard',
		},
		() => {}, // Execute handled by InputSystem
	);

	// Duplicate
	registerCommand(
		'clipboard.duplicate',
		{
			label: 'Duplicate',
			category: 'Canvas (Selection)',
			section: 'Edit',
			getLabel: (selectedNodes: UINode[]) =>
				nodeActions.getDuplicateLabel(selectedNodes.length),
		},
		() => {}, // Execute handled by InputSystem
	);

	// Undo
	registerCommand(
		'edit.undo',
		{
			label: 'Undo',
			category: 'Edit',
			section: 'History',
		},
		() => historyActions.undo(get(channel)),
	);

	input.on(
		'keydown',
		'mod+z',
		{
			mode: 'default',
			excludeModes: ['editing'],
			command: { id: 'edit.undo', name: 'Undo' },
			category: 'Edit',
		},
		(ctx) => {
			ctx.stopPropagation();
			historyActions.undo(get(channel));
		},
	);

	// Redo — Cmd+Shift+Z on Mac, Ctrl+Y on Windows/Linux
	registerCommand(
		'edit.redo',
		{
			label: 'Redo',
			category: 'Edit',
			section: 'History',
		},
		() => historyActions.redo(get(channel)),
	);

	input.on(
		'keydown',
		{ mac: 'mod+shift+z', default: 'mod+y' },
		{
			mode: 'default',
			excludeModes: ['editing'],
			command: { id: 'edit.redo', name: 'Redo' },
			category: 'Edit',
		},
		(ctx) => {
			ctx.stopPropagation();
			historyActions.redo(get(channel));
		},
	);
}

/**
 * Register debug/development commands.
 */
function registerDebugCommands(editor: CanvasEditor): void {
	registerCommand(
		'debug.log-nodes',
		{
			label: 'Debug: Log Nodes to Console',
			category: 'Canvas (Selection)',
			section: 'Debug',
			description: 'Log selected node data structures to the browser console',
			getLabel: (selectedNodes: UINode[]) =>
				selectedNodes.length > 0 ? 'Debug: Log Nodes to Console' : false,
		},
		() => {
			const selectedIds = editor.getSelection();
			const allNodes = editor.getNodes();
			const selectedNodes = allNodes.filter((n) => selectedIds.includes(n.id));

			// Get full UINode data from the store (includes type, data, etc.)
			const uiNodes = get(nodesStore);
			const uiNodeMap = new Map(uiNodes.map((n) => [n.id, n]));

			if (selectedNodes.length === 0) {
				console.log('[Debug] No selected nodes');
				return;
			}

			console.log(`[Debug] ${selectedNodes.length} selected node(s):`);
			for (const node of selectedNodes) {
				const uiNode = uiNodeMap.get(node.id);
				if (uiNode) {
					console.log(`[Debug] Node "${node.id}" (UINode):`, uiNode);
				}
				console.log(`[Debug] Node "${node.id}" (AppNode):`, node);
			}
		},
	);
}

// Re-export types and utilities
export * from './types';
export * from './registry';
export { buildPaletteActions, buildInputSystemActions } from './bridge';
